"use client"

import { logger } from "@/lib/logger"

import type React from "react"
import { createContext, useContext, useEffect, useRef, useState } from "react"
import { usePathname, useRouter } from "next/navigation"
import { supabase } from "@/lib/supabase-client"
import { clearAllClientCaches, clearCrmIndexedDB } from "@/lib/cache/client-cache-registry"
import { invalidateCrmObjectList } from "@/lib/crm/objects/object-list-cache"
import { getQueryClient } from "@/components/query-provider"
import { isMockAuthEnabled, MOCK_AUTH_USER } from "@/lib/auth/mock-auth"

interface User {
  id: string
  email: string
  user_metadata?: any
  app_metadata?: any
  aud: string
  created_at: string
  role: string
  updated_at: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  isSuperAdmin: boolean
  refreshAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isSuperAdmin: false,
  refreshAuth: async () => {},
})

// The local mock sign-in's account (lib/auth/mock-auth.ts)
const mockUser: User = {
  id: MOCK_AUTH_USER.id,
  email: MOCK_AUTH_USER.email,
  user_metadata: { name: `${MOCK_AUTH_USER.first_name} ${MOCK_AUTH_USER.last_name}` },
  app_metadata: {},
  aud: "authenticated",
  created_at: new Date().toISOString(),
  role: "authenticated",
  updated_at: new Date().toISOString(),
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const adminCheckedRef = useRef<string | null>(null)
  const pathname = usePathname()
  const router = useRouter()

  // Check admin status only once per user (avoids repeated API calls)
  const checkAdminOnce = async (userId: string) => {
    if (adminCheckedRef.current === userId) return
    adminCheckedRef.current = userId // Set immediately to block concurrent calls
    try {
      const response = await fetch('/api/auth/check-admin')
      const data = await response.json()
      setIsSuperAdmin(data.isSuperAdmin || false)
    } catch (error) {
      logger.error("Error checking super admin status:", error)
      setIsSuperAdmin(false)
      adminCheckedRef.current = null // Reset on error to allow retry
    }
  }

  // Function to refresh auth state
  const refreshAuth = async () => {
    if (isMockAuthEnabled()) {
      // Local mock sign-in: signed in on dashboard routes only
      const shouldBeLoggedIn = pathname?.startsWith("/dashboard")
      setUser(shouldBeLoggedIn ? mockUser : null)
      setLoading(false)
      setInitialized(true)
      return
    }

    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession()

      if (error) {
        logger.error("Error refreshing auth state:", error)
        setUser(null)
      } else {
        logger.debug("Refreshed auth state, user:", session?.user?.id || "No user")
        setUser((session?.user as User) || null)

        // Check super admin status if user is logged in
        if (session?.user) {
          await checkAdminOnce(session.user.id)
        } else {
          setIsSuperAdmin(false)
        }

        // If no session and on a protected route, redirect to home
        if (!session && pathname?.startsWith("/dashboard")) {
          router.push("/")
        }
      }
    } catch (error) {
      logger.error("Error refreshing auth state:", error)
      setUser(null)
    } finally {
      setLoading(false)
      setInitialized(true)
    }
  }

  // Initial auth setup
  // MAITE-511: install global client error listeners once at app boot
  useEffect(() => {
    import('@/lib/errors/client-error-reporter').then(({ installGlobalErrorReporter }) => {
      installGlobalErrorReporter()
    })
  }, [])

  useEffect(() => {
    if (isMockAuthEnabled()) {
      // Local mock sign-in: signed in on dashboard routes only
      const shouldBeLoggedIn = pathname?.startsWith("/dashboard")
      setUser(shouldBeLoggedIn ? mockUser : null)
      setLoading(false)
      setInitialized(true)
      return
    }

    // Subscribe FIRST, synchronously. This listener was previously created
    // inside the async body below and its unsubscribe closure discarded, so
    // every pathname change stacked one more permanent listener — after N
    // navigations a single SIGNED_OUT ran the cache wipe and redirect N
    // times. Now the effect cleanup really unsubscribes, which is exactly
    // why the re-subscribe must be synchronous: React tears the previous
    // effect down and runs this one in the same commit, so no window exists
    // in which a SIGNED_OUT (e.g. a failed token refresh, possibly emitted
    // from another tab) fires with zero listeners. The SIGNED_OUT branch is
    // the app's ONLY cross-account cache wipe — dropping it leaves the
    // previous account's caches readable.
    let debounceTimer: NodeJS.Timeout | null = null
    let isMounted = true // Track component mount state to prevent stale updates
    // A getSession() still in flight when SIGNED_OUT lands must not set the
    // stale user back after the wipe.
    let sawSignOut = false

    const handleSignedOut = () => {
      sawSignOut = true
      setUser(null)
      setIsSuperAdmin(false)
      adminCheckedRef.current = null

      // Clear all client-side caches to prevent cross-account data leakage
      if (typeof window !== 'undefined') {
        try {
          // Clear React Query in-memory cache
          getQueryClient().clear();

          // Clear the module-level CRM object cache, which clearAllClientCaches does not cover
          invalidateCrmObjectList();

          // Clear all localStorage, sessionStorage, and pattern-matched keys
          clearAllClientCaches();

          // Also clear organization storage (kept explicit for clarity)
          localStorage.removeItem('organization-storage');
          localStorage.removeItem('selectedOrganization');

          // Clear CRM IndexedDB databases (async, fire-and-forget)
          clearCrmIndexedDB();

          logger.debug('Cleared all caches on sign-out');
        } catch (error) {
          logger.error('Error clearing caches on sign-out:', error);
        }
      }
      if (pathname?.startsWith("/dashboard")) {
        router.push("/")
      }
    }

    // Typed off the SDK signature so `event` / `session` keep their inference
    // without a new import.
    const handleAuthStateChange: Parameters<typeof supabase.auth.onAuthStateChange>[0] = (
      event,
      session,
    ) => {
      // Clear previous timer
      if (debounceTimer) {
        clearTimeout(debounceTimer)
        debounceTimer = null
      }

      // Skip processing if component is unmounted
      if (!isMounted) return

      // TOKEN_REFRESHED is a normal event during session refresh - don't treat as state change
      // unless the session actually changed
      if (event === "TOKEN_REFRESHED") {
        if (process.env.NODE_ENV !== 'production') {
          logger.debug("Token refreshed, session still valid:", session?.user?.id)
        }
        // Only update if we have a valid session - don't set to null during refresh
        if (session?.user) {
          setUser(session.user as User)
        }
        return
      }

      // SIGNED_OUT is handled immediately, NOT debounced: the debounce timer
      // is cleared on unmount, so a navigation issued right after signOut()
      // would silently drop the cache wipe.
      if (event === "SIGNED_OUT") {
        if (process.env.NODE_ENV !== 'production') {
          logger.debug("Auth state changed:", event)
        }
        handleSignedOut()
        return
      }

      // Debounce the remaining auth state changes to prevent rapid updates
      debounceTimer = setTimeout(() => {
        // Double-check mount state after timeout
        if (!isMounted) return

        if (process.env.NODE_ENV !== 'production') {
          logger.debug("Auth state changed:", event, session?.user?.id)
        }

        if (event === "SIGNED_IN" && session?.user) {
          setUser(session.user as User)
          invalidateCrmObjectList()
          // Check super admin status on sign in
          checkAdminOnce(session.user.id)
        } else if (session?.user) {
          // Other events with valid session - just update user
          setUser(session.user as User)
        }
      }, 100) // 100ms debounce
    }

    // `supabase` is a lazy Proxy: the first property access builds the browser
    // client, and that construction RE-THROWS when NEXT_PUBLIC_SUPABASE_URL or
    // _ANON_KEY is missing/invalid (lib/supabase-client.ts). Subscribing
    // synchronously moved that first access out of initializeAuth's try, and
    // AuthProvider is global — letting it escape here would take every route,
    // public pages included, into an error boundary instead of the logged-out
    // fallback this app has always degraded to. initializeAuth below hits the
    // same throw inside its own try, whose catch/finally IS that fallback, so
    // this only has to not escape.
    let subscription: { unsubscribe: () => void } | null = null
    try {
      subscription = supabase.auth.onAuthStateChange(handleAuthStateChange).data.subscription
    } catch (error) {
      logger.error("Error subscribing to auth state changes:", error)
    }

    const initializeAuth = async () => {
      try {
        // Get initial session
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession()

        // A pathname change replaces this effect while getSession() is still in
        // flight, and the replacement owns auth state from that moment. Any
        // SIGNED_OUT since then went to ITS listener, so it set ITS sawSignOut —
        // this closure's stayed false and its own listener is already
        // unsubscribed. Writing below would restore the signed-out user after the
        // wipe, and on a public route handleSignedOut does not redirect, so
        // nothing would correct it. sawSignOut alone only guards within one run.
        if (!isMounted) return

        if (error) {
          logger.error("Error getting initial session:", error)
          setUser(null)
        } else if (!sawSignOut) {
          if (process.env.NODE_ENV !== 'production') {
            logger.debug("Initial session user:", session?.user?.id || "No user")
          }
          setUser((session?.user as User) || null)

          // Check super admin status if user is logged in
          if (session?.user) {
            await checkAdminOnce(session.user.id)
          } else {
            setIsSuperAdmin(false)
          }

          // If no session and on a protected route, redirect to home
          if (!session && pathname?.startsWith("/dashboard")) {
            router.push("/")
          }
        }
      } catch (error) {
        logger.error("Error initializing auth:", error)
        setUser(null)
      } finally {
        setLoading(false)
        setInitialized(true)
      }
    }

    initializeAuth()

    return () => {
      isMounted = false
      if (debounceTimer) {
        clearTimeout(debounceTimer)
        debounceTimer = null
      }
      subscription?.unsubscribe()
    }
  }, [pathname, router])

  return (
    <AuthContext.Provider value={{ user, loading: loading || !initialized, isSuperAdmin, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  )
}
